import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  doc,
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
  type DocumentData
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../auth-context';
import AddItemForm from '../components/AddItemForm';
import ReactMarkdown from 'react-markdown';
import { explainItem, suggestNotesFromScreenshot, translateText } from '../ai';
import type { Checklist, ChecklistItem, ItemStatus } from '../types';

export default function ChecklistDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const cUnsub = onSnapshot(doc(db, 'checklists', id), (snap) => {
      if (snap.exists()) {
        setChecklist({ id: snap.id, ...snap.data() } as Checklist);
      }
    });

    const iQ = query(collection(db, 'checklists', id, 'items'), orderBy('order'));
    const iUnsub = onSnapshot(iQ, (snap) => {
      setItems(
        snap.docs.map((d) => ({ id: d.id, checklistId: id, ...d.data() }) as ChecklistItem)
      );
      setLoading(false);
    });

    return () => {
      cUnsub();
      iUnsub();
    };
  }, [id]);

  async function updateItem(itemId: string, patch: Partial<DocumentData>) {
    if (!id || !user) return;
    const item = items.find((i) => i.id === itemId);
    const oldStatus = item?.status;
    const newStatus = (patch.status as ItemStatus | undefined) ?? oldStatus;

    await updateDoc(doc(db, 'checklists', id, 'items', itemId), {
      ...patch,
      completedBy: user.uid,
      completedAt: serverTimestamp()
    });

    if (oldStatus && newStatus && oldStatus !== newStatus) {
      const inc: Record<string, ReturnType<typeof increment> | unknown> = {};
      const dec = (s: ItemStatus) => {
        if (s === 'pass') inc.passCount = increment(-1);
        else if (s === 'fail') inc.failCount = increment(-1);
        else if (s === 'pending') inc.pendingCount = increment(-1);
      };
      const add = (s: ItemStatus) => {
        if (s === 'pass') inc.passCount = increment(1);
        else if (s === 'fail') inc.failCount = increment(1);
        else if (s === 'pending') inc.pendingCount = increment(1);
      };
      dec(oldStatus);
      add(newStatus);
      inc.updatedAt = serverTimestamp();
      await updateDoc(doc(db, 'checklists', id), inc as Record<string, ReturnType<typeof increment>>);
    }
  }

  async function uploadScreenshot(itemId: string, file: File): Promise<void> {
    if (!id) return;
    const path = `screenshots/${id}/${itemId}/${Date.now()}-${file.name}`;
    const r = ref(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    await updateItem(itemId, { screenshotUrl: url, screenshotPath: path });
  }

  async function removeScreenshot(itemId: string): Promise<void> {
    if (!id) return;
    const item = items.find((i) => i.id === itemId);
    const path = (item as unknown as { screenshotPath?: string })?.screenshotPath;
    if (path) {
      try { await deleteObject(ref(storage, path)); } catch { /* ignore — file may already be gone */ }
    }
    await updateItem(itemId, { screenshotUrl: null, screenshotPath: null });
  }

  async function deleteItem(itemId: string): Promise<void> {
    if (!id) return;
    const item = items.find((i) => i.id === itemId);
    // remove storage screenshot if any
    const path = (item as unknown as { screenshotPath?: string })?.screenshotPath;
    if (path) {
      try { await deleteObject(ref(storage, path)); } catch { /* ignore */ }
    }
    await deleteDoc(doc(db, 'checklists', id, 'items', itemId));
    // decrement counters
    const dec: Record<string, ReturnType<typeof increment> | unknown> = {
      itemCount: increment(-1),
      updatedAt: serverTimestamp()
    };
    if (item?.status === 'pass') dec.passCount = increment(-1);
    else if (item?.status === 'fail') dec.failCount = increment(-1);
    else if (item?.status === 'pending') dec.pendingCount = increment(-1);
    await updateDoc(doc(db, 'checklists', id), dec as Record<string, ReturnType<typeof increment>>);
  }

  if (loading || !checklist) {
    return <div className="empty">Loading…</div>;
  }

  return (
    <div className="page page--detail">
      <div className="page__breadcrumb">
        <Link to="/checklists" className="breadcrumb">
          ← All checklists
        </Link>
      </div>

      <div className="page__header">
        <div>
          <div className="page__tags">
            <span className={`tag tag--${checklist.project.toLowerCase()}`}>
              {checklist.project}
            </span>
            <span className="tag tag--ghost">{checklist.status}</span>
          </div>
          <h1 className="page__title">{checklist.title}</h1>
          {checklist.description && (
            <p className="page__subtitle">{checklist.description}</p>
          )}
        </div>
      </div>

      <ul className="item-list">
        {items.length === 0 && (
          <li className="empty empty--inline">
            No items yet.{' '}
            {profile?.role === 'admin' ? 'Add the first one below.' : 'Waiting on admin to add items.'}
          </li>
        )}
        {items.map((item, idx) => (
          <ChecklistItemCard
            key={item.id}
            item={item}
            index={idx}
            project={checklist.project}
            onStatusChange={(status) => updateItem(item.id, { status })}
            onNotesChange={(qcNotes) => updateItem(item.id, { qcNotes })}
            onUpload={(file) => uploadScreenshot(item.id, file)}
            onRemoveScreenshot={() => removeScreenshot(item.id)}
            onDelete={profile?.role === 'admin' ? () => deleteItem(item.id) : undefined}
          />
        ))}
      </ul>

      {profile?.role === 'admin' && id && (
        <AddItemForm checklistId={id} nextOrder={items.length} />
      )}
    </div>
  );
}

interface ItemCardProps {
  item: ChecklistItem;
  index: number;
  project: string;
  onStatusChange: (status: ItemStatus) => void;
  onNotesChange: (notes: string) => void;
  onUpload: (file: File) => Promise<void>;
  onRemoveScreenshot: () => Promise<void>;
  onDelete?: () => Promise<void>;
}

function ChecklistItemCard({ item, index, project, onStatusChange, onNotesChange, onUpload, onRemoveScreenshot, onDelete }: ItemCardProps) {
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainText, setExplainText] = useState<string | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState<string | null>(null);

  const [notes, setNotes] = useState(item.qcNotes || '');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  // 🇷🇴 Translation toggle
  const [showRo, setShowRo] = useState(false);
  const [roDesc, setRoDesc] = useState<string | null>(null);
  const [roCriteria, setRoCriteria] = useState<string | null>(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  async function handleTranslate() {
    setTranslateError(null);
    if (showRo) {
      setShowRo(false);
      return;
    }
    if (roDesc) {
      setShowRo(true);
      return;
    }
    setTranslateLoading(true);
    try {
      const desc = await translateText({ text: item.description, targetLang: 'ro', context: 'QC checklist item for a frontend reviewer' });
      setRoDesc(desc);
      if (item.acceptanceCriteria) {
        const cr = await translateText({ text: item.acceptanceCriteria, targetLang: 'ro', context: 'expected/acceptance criteria for QC item' });
        setRoCriteria(cr);
      }
      setShowRo(true);
    } catch (e) {
      setTranslateError(e instanceof Error ? e.message : 'Translation failed');
    } finally {
      setTranslateLoading(false);
    }
  }

  async function handleExplain() {
    setExplainOpen(true);
    if (explainText) return;
    setExplainLoading(true);
    setExplainError(null);
    try {
      const text = await explainItem({
        itemDescription: item.description,
        acceptanceCriteria: item.acceptanceCriteria,
        project
      });
      setExplainText(text);
    } catch (e) {
      setExplainError(e instanceof Error ? e.message : 'AI is unavailable right now');
    } finally {
      setExplainLoading(false);
    }
  }

  async function handleUpload(file: File) {
    await onUpload(file);
  }

  async function handleSuggestNotes() {
    if (!item.screenshotUrl) {
      setSuggestError('Upload a screenshot first.');
      return;
    }
    setSuggestLoading(true);
    setSuggestError(null);
    try {
      // Fetch the screenshot back as base64 (Firebase Storage gives us a public download URL)
      const res = await fetch(item.screenshotUrl);
      const blob = await res.blob();
      const base64 = await blobToBase64(blob);
      const suggested = await suggestNotesFromScreenshot({
        imageBase64: base64,
        imageMimeType: blob.type || 'image/png',
        itemDescription: item.description,
        acceptanceCriteria: item.acceptanceCriteria,
        project
      });
      setNotes(suggested);
      onNotesChange(suggested);
    } catch (e) {
      setSuggestError(e instanceof Error ? e.message : 'Could not analyze screenshot');
    } finally {
      setSuggestLoading(false);
    }
  }

  return (
    <li className={`item item--${item.status}`}>
      <div className="item__head">
        <span className="item__number">{index + 1}</span>
        <div className="item__body">
          <p className="item__desc">{showRo && roDesc ? roDesc : item.description}</p>
          {item.acceptanceCriteria && (
            <p className="item__criteria">
              <strong>{showRo && roDesc ? 'Așteptat:' : 'Expected:'}</strong> {showRo && roCriteria ? roCriteria : item.acceptanceCriteria}
            </p>
          )}
          <div className="item__triggers">
            <button className="ai-trigger" onClick={handleExplain}>
              💡 Help me understand
            </button>
            <button className="ai-trigger ai-trigger--ro" disabled={translateLoading} onClick={handleTranslate}>
              {translateLoading
                ? '🇷🇴 translating…'
                : showRo
                ? '🇬🇧 English'
                : '🇷🇴 Română'}
            </button>
          </div>
          {translateError && <div className="ai-panel__error">{translateError}</div>}
          {explainOpen && (
            <div className="ai-panel">
              {explainLoading && <div className="ai-panel__loading">thinking…</div>}
              {explainError && <div className="ai-panel__error">{explainError}</div>}
              {explainText && (
                <div className="ai-panel__body">
                  <ReactMarkdown>{explainText}</ReactMarkdown>
                </div>
              )}
              <button className="ai-panel__close" onClick={() => setExplainOpen(false)}>
                ✕ close
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="item__actions">
        <ItemStatusButtons current={item.status} onChange={onStatusChange} />
        {onDelete && (
          <button
            className="item__delete"
            title="Delete item"
            onClick={async () => {
              if (!confirm(`Delete item "${item.description.slice(0, 60)}..."? This can't be undone.`)) return;
              await onDelete();
            }}
          >
            ✕ delete
          </button>
        )}
      </div>

      <div className="item__notes">
        <textarea
          placeholder="Notes (what you saw, steps to reproduce, etc)…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => onNotesChange(notes)}
        />

        <div className="item__notes-actions">
          {!item.screenshotUrl ? (
            <label className="item__upload">
              <span>📎 Add screenshot</span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                }}
              />
            </label>
          ) : (
            <span className="item__upload item__upload--attached">
              <a href={item.screenshotUrl} target="_blank" rel="noreferrer">
                📎 Screenshot attached
              </a>
              <button
                type="button"
                className="item__upload-remove"
                title="Remove screenshot"
                onClick={onRemoveScreenshot}
              >
                ✕
              </button>
            </span>
          )}

          {item.screenshotUrl && (
            <button className="ai-trigger" disabled={suggestLoading} onClick={handleSuggestNotes}>
              {suggestLoading ? '✨ analyzing…' : '✨ Suggest notes from screenshot'}
            </button>
          )}
        </div>
        {suggestError && <div className="ai-panel__error">{suggestError}</div>}
      </div>
    </li>
  );
}

function ItemStatusButtons({
  current,
  onChange
}: {
  current: ItemStatus;
  onChange: (s: ItemStatus) => void;
}) {
  const opts: { value: ItemStatus; label: string }[] = [
    { value: 'pass', label: '✓ Pass' },
    { value: 'fail', label: '✗ Fail' },
    { value: 'skip', label: '⊘ Skip' }
  ];
  return (
    <div className="status-buttons">
      {opts.map((o) => (
        <button
          key={o.value}
          className={`status-btn status-btn--${o.value} ${current === o.value ? 'is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      // strip the data: URL prefix
      const comma = result.indexOf(',');
      resolve(result.slice(comma + 1));
    };
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
